/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CML, CMLUtils } from '@coursera-web/cml';
import gql from 'graphql-tag';
import moment from 'moment';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { MED_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Grid, Typography2, breakpoints } from '@coursera/cds-core';
import { ThumbsUpFilledIcon, ThumbsUpIcon } from '@coursera/cds-icons';
import { StarRating } from '@coursera/coursera-ui';

import { ENROLL } from 'bundles/enroll-common/utils/enrollActionParams';
import withFragments from 'bundles/graphql/components/withFragments';
import { HELPFUL_VOTE, UNMARKED_VOTE } from 'bundles/reviews-common/constants';
import type { ProductReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import { submitResponse } from 'bundles/reviews-common/utils/ReviewHelpfulAPIUtils';

import _t from 'i18n!nls/reviews-common';

const styles = {
  reviewInfo: css`
    -webkit-box-align: center;
    align-items: center;
    display: flex;
  `,
  reviewText: () => css`
    padding-right: var(--cds-spacing-150);
    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  helpfulColumn: () => css`
    display: flex;
    align-items: center;
    padding-left: var(--cds-spacing-150);
    ${breakpoints.down('sm')} {
      padding-left: 0;
    }
  `,
  reviewHelpfulButton: () => css`
    min-width: 230px;

    &:hover,
    &:focus {
      color: var(--cds-color-blue-800);
      background-color: var(--cds-color-blue-25);
    }
  `,
};

type InputProps = {
  userId?: number;
};

type Props = {
  review: ProductReview;
  userId?: number;
  router: InjectedRouter;
};

type State = {
  helpfulCount: number;
  isMarkedHelpful: boolean;
};

/**
 * A legacy React component that renders a product review with star rating, author info, review text, and helpful voting functionality.
 * Includes features for displaying review metadata, formatted dates, and interactive voting buttons.
 */
export class Review extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof Review.contextTypes>;

  constructor(props: Props, context?: { executeAction: () => void }) {
    super(props, context);
    const { review } = props as Props;

    this.state = {
      helpfulCount: review.mostHelpfulVoteCount && review.mostHelpfulVoteCount > 0 ? review.mostHelpfulVoteCount : 0,
      isMarkedHelpful: review.isMarkedHelpful,
    };
  }

  handleHelpfulButtonClick = (userId: number, reviewId: string, voteType: string) => {
    const { executeAction } = this.context;

    executeAction(submitResponse, {
      reviewData: {
        userId,
        reviewId,
      },
      voteType,
      onSuccess: voteType === 'HELPFUL_VOTE' ? this.incrementCount : this.decrementCount,
    });
  };

  handleLogOutButtonClick = () => {
    const { router } = this.props;

    router.replace({
      pathname: router.location.pathname,
      query: { ...router.location.query, authMode: 'signup', action: ENROLL },
    });
  };

  getAuthorDisplayName = (authorName: string) => {
    if (authorName === undefined || authorName === null) {
      return null;
    }

    const splitName = authorName.split(' ');
    let displayName = '';

    for (let i = 0; i < splitName.length; i += 1) {
      if (i === 0) {
        displayName = `${splitName[i]}`;
      } else {
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        displayName += ` ${splitName[i].substring(0, 1)}`;
      }
    }

    return displayName;
  };

  incrementCount = () => {
    const { helpfulCount } = this.state;
    this.setState(() => ({
      helpfulCount: helpfulCount + 1,
      isMarkedHelpful: true,
    }));
  };

  decrementCount = () => {
    const { helpfulCount } = this.state;
    this.setState(() => ({
      helpfulCount: helpfulCount - 1,
      isMarkedHelpful: false,
    }));
  };

  render() {
    const { review, userId } = this.props;
    const { helpfulCount, isMarkedHelpful } = this.state;

    const helpfulCountLabel = helpfulCount && helpfulCount > 0 ? `(${helpfulCount})` : '';
    const voteType = isMarkedHelpful ? UNMARKED_VOTE : HELPFUL_VOTE;

    const reviewerPublicFullName = (review || {}).fullName;

    if (!reviewerPublicFullName) {
      return null;
    }

    const starRatingProps = {
      rating: review.rating,
      a11yIdentifier: `${review.fullName}`,
      enableColorOpacity: true,
    };

    return (
      <Grid container className="review review-text review-page-review m-b-2">
        <Grid item md={8} css={styles.reviewText}>
          <Grid item alignItems="center" css={styles.reviewInfo}>
            <StarRating {...starRatingProps} />
            <Typography2 component="p" className="reviewerName p-x-1s" variant="bodySecondary" color="supportText">
              <FormattedMessage message={_t('By {author}')} author={this.getAuthorDisplayName(review.fullName)} />
            </Typography2>
            &bull;
            <Typography2 component="p" className="dateOfReview p-x-1s" variant="bodySecondary" color="supportText">
              {formatDateTimeDisplay(moment(review.reviewedAt).locale(_t.getLocale()), MED_DATE_ONLY_DISPLAY)}
            </Typography2>
          </Grid>
          <div className="reviewText">
            <CML className="font-lg" cml={CMLUtils.create(review.reviewText.cml.value, review.reviewText.cml.dtdId)} />
          </div>
        </Grid>
        <Grid item className="e2e-helpful-button-col" md={4} css={styles.helpfulColumn}>
          <Button
            className="review-helpful-button"
            css={styles.reviewHelpfulButton}
            icon={isMarkedHelpful ? <ThumbsUpFilledIcon color="interactive" /> : <ThumbsUpIcon color="interactive" />}
            iconPosition="before"
            variant="secondary"
            onClick={() => {
              return !userId
                ? this.handleLogOutButtonClick()
                : this.handleHelpfulButtonClick(userId, review.id, voteType);
            }}
          >
            <FormattedMessage message={_t('This is helpful {helpfulCount}')} helpfulCount={helpfulCountLabel} />
          </Button>
        </Grid>
      </Grid>
    );
  }
}

/**
 * GraphQL fragment intended to be used with withFragment. Defines the shape of review data required by the Review component.
 * Includes fields for review metadata, content, and voting information.
 */
export const ReviewFragment = gql`
  fragment ReviewFragment on ProductReviewsV1 {
    id
    reviewedAt
    rating
    isMarkedHelpful
    reviewText {
      ... on ProductReviewsV1_cmlMember {
        cml {
          dtdId
          value
        }
      }
    }
    productCompleted
    mostHelpfulVoteCount
    fullName
  }
`;

/**
 * Enhanced Review legacy HoC component wrapped with router connectivity and GraphQL fragment integration.
 * Provides routing capabilities and structured data fetching for the base Review component.
 */
export default compose<Props, InputProps>(
  connectToRouter((router) => ({ router })),
  withFragments({
    review: ReviewFragment,
  })
)(Review);
